import style from './ChartContextMenu.module.css';

import {
  Menu,
  Item,
  Separator,
  Submenu,
  theme,
  animation, useContextMenu,
} from 'react-contexify';

import 'react-contexify/dist/ReactContexify.css';
import { useWebsocketStore } from '../../../../../../../stores/websocketStore/websocketProvider';
import { closePctPosition } from '../../../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/updateTradePlanMessages/closePctPosition';
import { observer } from 'mobx-react-lite';
import { addPositionBreakPriceFromChart } from '../../../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/updateTradePlanMessages/addPositionBreakPriceFromChart';
import { useTradePlanFormStore } from '../../../../../../../classes/tradePlan/tradePlanProvider';
import { TradePlanData } from '../../../../../../../classes/tradePlan/tradePlanData';
import { addBreakSetup } from './BreakBounceSetup/addBreak';
import { addBounceSetup } from './BreakBounceSetup/addBounce';
import { addCandleBreak } from './candleBreak/candleBreak';
interface Props {
  candleDuration: number;
  timeUnit: string;
  autoSend?: boolean;
  scaleY?: { invert: (value: number) => number };
  tradePlanId: string | number;
  hash: string;
}
const timeUnitMap = {
  MINUTES: 'm',
  DAYS: 'D',
  WEEKS: 'W',
  MONTHS: 'M',
  QUARTERS: 'Q',
  YEARS: 'Y',
};
export const ChartContextMenu = observer(
  ({
    timeUnit,
    candleDuration,
    scaleY,
    tradePlanId,
    autoSend = false,
    hash,
  }: Props) => {
    // explicitly
    // popup with trade plan info
    //
    // NOTE Is this where we load a selected TPT based on the combobox at the top of Chart Trading?
    let tradePlanForm: TradePlanData | null = null;     // NOTE is the default empty, which is why we don't get User Prefs?
    if (tradePlanId === 'newTradePlan') {
      tradePlanForm = useTradePlanFormStore();
    }
    const websocketStore = useWebsocketStore();
    const { tradePlanStore, priceDataStore } = websocketStore;
    const currentPrice = priceDataStore.trades[hash]?.price ?? null;
    const direction = tradePlanForm?.direction ?? null;
    const tradePlan = tradePlanStore.tradePlans[tradePlanId] ?? null;
    const inPosition = tradePlan?.inPosition;
    const id = tradePlan?.id;
    const totalTradePlanQty = tradePlan?.totalTradePlanQty;
    const symbol = tradePlan?.symbol;

    const { show } = useContextMenu({
      id: tradePlanId
    });
    function displayMenu(e) {
      show(e)
    }
    // TODO can't we get the triggered object, so we can handle all of these in a single function?
    function isBounceHidden({
                        triggerEvent,
                        props,
                        data
                      }){
      const clickPrice = scaleY.invert(props.yCoordinate);
      const cannotBounce = ( clickPrice > currentPrice && !tradePlanForm.goShort ) || ( clickPrice < currentPrice && !tradePlanForm.goLong )
      // console.log(`isBounceHidden ${clickPrice} ${currentPrice} ${tradePlanForm.goLong} ${tradePlanForm.goShort} ${cannotBounce}\n${JSON.stringify(triggerEvent)}\n${JSON.stringify(props)}\n${JSON.stringify(data)}\n`)
      return cannotBounce
    }
    function isBreakHidden({
                        triggerEvent,
                        props,
                        data
                      }){
      const clickPrice = scaleY.invert(props.yCoordinate);
      const cannotBreak = ( clickPrice < currentPrice && !tradePlanForm.goShort ) || ( clickPrice > currentPrice && !tradePlanForm.goLong )
      // console.log(`isBreakHidden ${clickPrice} ${currentPrice} ${tradePlanForm.goLong} ${tradePlanForm.goShort} ${cannotBreak}\n${JSON.stringify(triggerEvent)}\n${JSON.stringify(props)}\n${JSON.stringify(data)}\n`)
      return cannotBreak
    }
    function isCBLongHidden({
                        triggerEvent,
                        props,
                        data
                      }){
      const clickPrice = scaleY.invert(props.yCoordinate);
      const cannotCBLong = ( !tradePlanForm.goLong )
      // console.log(`isCBLongHidden ${clickPrice} ${currentPrice} ${tradePlanForm.goLong} ${tradePlanForm.goShort} ${cannotCBLong}\n${JSON.stringify(triggerEvent)}\n${JSON.stringify(props)}\n${JSON.stringify(data)}\n`)
      return cannotCBLong
    }
    function isCBShortHidden({
                        triggerEvent,
                        props,
                        data
                      }){
      const clickPrice = scaleY.invert(props.yCoordinate);
      const cannotCBShort = ( !tradePlanForm.goShort )
      // console.log(`isCBShortHidden ${clickPrice} ${currentPrice} ${tradePlanForm.goLong} ${tradePlanForm.goShort} ${cannotCBShort}\n${JSON.stringify(triggerEvent)}\n${JSON.stringify(props)}\n${JSON.stringify(data)}\n`)
      return cannotCBShort
    }
    function isCBBothHidden({
                        triggerEvent,
                        props,
                        data
                      }){
      const clickPrice = scaleY.invert(props.yCoordinate);
      const cannotCBBoth = ( !tradePlanForm.goShort ) || ( !tradePlanForm.goLong )
      // console.log(`isCBBothHidden ${clickPrice} ${currentPrice} ${tradePlanForm.goLong} ${tradePlanForm.goShort} ${cannotCBBoth}\n${JSON.stringify(triggerEvent)}\n${JSON.stringify(props)}\n${JSON.stringify(data)}\n`)
      return cannotCBBoth
    }
    function addTarget({ props, data }) {
      const price = scaleY.invert(props.yCoordinate);
      closePctPosition(id, data, symbol, price, false);
    }
    function addTargetAndReplace({ props, data }) {
      const price = scaleY.invert(props.yCoordinate);
      closePctPosition(id, data, symbol, price, true);
    }
    function addStop({ props }) {
      const price = scaleY.invert(props.yCoordinate);
      addPositionBreakPriceFromChart(id, price, symbol);
    }

    function addBreakPrice({ props }) {
      if (currentPrice === null) {
        tradePlanForm.setValidationStatus({
          status: false,
          message: 'Issue obtaining current price with selected data source.',
        });
      }
      const price = scaleY.invert(props.yCoordinate);
      if (price < currentPrice) {
        if (direction === 'LONG' || direction === 'BOTH') {
          tradePlanForm.addExitTactic('BREAK_PRICE', { prices: [price] });
        } else if (direction === 'SHORT') {
          tradePlanForm.setValidationStatus({
            status: false,
            message: 'Break price is already below current price.',
          });
        } else {
          tradePlanForm.setDirection('LONG');
          tradePlanForm.addExitTactic('BREAK_PRICE', { prices: [price] });
        }
      } else if (price > currentPrice) {
        if (direction === 'SHORT' || direction === 'BOTH') {
          tradePlanForm.addExitTactic('BREAK_PRICE', { prices: [price] });
        } else if (direction === 'LONG') {
          tradePlanForm.setValidationStatus({
            status: false,
            message: 'Break price is already above current price.',
          });
        } else {
          tradePlanForm.setDirection('SHORT');
          tradePlanForm.addExitTactic('BREAK_PRICE', { prices: [price] });
        }
      }
    }
    if (tradePlanId === 'newTradePlan') {
      return (
        <div>
        <div onContextMenu={show}>
        </div>
        <div onContextMenu={displayMenu}>
        </div>

        <Menu
          id={tradePlanId}
          theme={theme.dark}
          animation={animation.flip}
          className={style.menu}
        >
          <Item disabled> Add </Item>
          <Submenu label="Setup">
            <Item
              hidden={isBreakHidden}
              onClick={({ props }) =>
                addBreakSetup({
                  contextMenuProps: props,
                  currentPrice,
                  tradePlanForm: tradePlanForm,
                  autoSend,
                  scaleY,
                  websocketStore,
                })
              }
            >
              Break
            </Item>
            <Item
              hidden={isBounceHidden}
              onClick={({ props }) =>
                addBounceSetup({
                  contextMenuProps: props,
                  currentPrice,
                  tradePlanForm: tradePlanForm,
                  autoSend,
                  scaleY,
                  websocketStore,
                })
              }
            >
              Bounce
            </Item>
            <Submenu
              label={`Break of previous ${candleDuration}${timeUnitMap[timeUnit]} candle`}
            >
              <Item
                data="LONG"
                hidden={isCBLongHidden}
                onClick={({ data }) =>
                  addCandleBreak({
                    data,
                    tradePlanForm,
                    websocketStore,
                    candleDuration,
                    timeUnit,
                    autoSend,
                  })
                }
              >
                Long
              </Item>
              <Item
                data="SHORT"
                hidden={isCBShortHidden}
                onClick={({ data }) =>
                  addCandleBreak({
                    data,
                    tradePlanForm,
                    websocketStore,
                    candleDuration,
                    timeUnit,
                    autoSend,
                  })
                }
              >
                Short
              </Item>
              <Item
                data="BOTH"
                hidden={isCBBothHidden}
                onClick={({ data }) =>
                  addCandleBreak({
                    data,
                    tradePlanForm,
                    websocketStore,
                    candleDuration,
                    timeUnit,
                    autoSend,
                  })
                }
              >
                Both
              </Item>
            </Submenu>
          </Submenu>
          <Submenu label="Exit">
            <Item
              onClick={({ props }) => {
                addBreakPrice({ props });
              }}
            >
              Break Price
            </Item>
          </Submenu>
        </Menu>
        </div>
      );
    }
    if (!inPosition)
      return (
        <Menu
          id={tradePlanId}
          theme={theme.dark}
          animation={animation.flip}
          className={style.menu}
        >
          <Item disabled> Add </Item>
          <Separator />
        </Menu>
      );
    // if (tradePlan.instrument === 'option')
    //   return (
    //     <Menu
    //       id={tradePlanId}
    //       theme={theme.dark}
    //       animation={animation.flip}
    //       className={style.menu}
    //     >
    //       <Item disabled> Add </Item>
    //       <Separator />
    //       <Submenu label="Target">
    //         <Item data={0.1} onClick={addTarget}>{`10%[${Math.ceil(
    //           totalTradePlanQty * 0.1
    //         )}]`}</Item>
    //         <Item data={0.25} onClick={addTarget}>{`25%[${Math.ceil(
    //           totalTradePlanQty * 0.25
    //         )}]`}</Item>
    //         <Item data={0.5} onClick={addTarget}>{`50%[${Math.ceil(
    //           totalTradePlanQty * 0.5
    //         )}]`}</Item>
    //         <Item data={0.75} onClick={addTarget}>{`75%[${Math.ceil(
    //           totalTradePlanQty * 0.75
    //         )}]`}</Item>
    //       </Submenu>
    //       <Submenu label="Target + Replace">
    //         <Item data={0.1} onClick={addTargetAndReplace}>{`10%[${Math.ceil(
    //           totalTradePlanQty * 0.1
    //         )}]`}</Item>
    //         <Item data={0.25} onClick={addTargetAndReplace}>{`25%[${Math.ceil(
    //           totalTradePlanQty * 0.25
    //         )}]`}</Item>
    //         <Item data={0.5} onClick={addTargetAndReplace}>{`50%[${Math.ceil(
    //           totalTradePlanQty * 0.5
    //         )}]`}</Item>
    //         <Item data={0.75} onClick={addTargetAndReplace}>{`75%[${Math.ceil(
    //           totalTradePlanQty * 0.75
    //         )}]`}</Item>
    //       </Submenu>
    //       <Item onClick={addStop}>Stop</Item>
    //     </Menu>
    //   );
    return (
      <Menu
        id={tradePlanId}
        theme={theme.dark}
        animation={animation.flip}
        className={style.menu}
      >
        <Item disabled> Add </Item>
        <Separator />
        <Submenu label="Target">
          <Item
            data={0.1}
            onClick={({ data, props }) => addTarget({ data, props })}
          >{`10%[${Math.ceil(totalTradePlanQty * 0.1)}]`}</Item>
          <Item
            data={0.25}
            onClick={({ data, props }) => addTarget({ data, props })}
          >{`25%[${Math.ceil(totalTradePlanQty * 0.25)}]`}</Item>
          <Item
            data={0.5}
            onClick={({ data, props }) => addTarget({ data, props })}
          >{`50%[${Math.ceil(totalTradePlanQty * 0.5)}]`}</Item>
          <Item
            data={0.75}
            onClick={({ data, props }) => addTarget({ data, props })}
          >{`75%[${Math.ceil(totalTradePlanQty * 0.75)}]`}</Item>
        </Submenu>
        <Submenu label="Target + Replace">
          <Item
            data={0.1}
            onClick={({ data, props }) => addTargetAndReplace({ data, props })}
          >{`10%[${Math.ceil(totalTradePlanQty * 0.1)}]`}</Item>
          <Item
            data={0.25}
            onClick={({ data, props }) => addTargetAndReplace({ data, props })}
          >{`25%[${Math.ceil(totalTradePlanQty * 0.25)}]`}</Item>
          <Item
            data={0.5}
            onClick={({ data, props }) => addTargetAndReplace({ data, props })}
          >{`50%[${Math.ceil(totalTradePlanQty * 0.5)}]`}</Item>
          <Item
            data={0.75}
            onClick={({ data, props }) => addTargetAndReplace({ data, props })}
          >{`75%[${Math.ceil(totalTradePlanQty * 0.75)}]`}</Item>
        </Submenu>
        <Item onClick={({ props }) => addStop({ props })}>Stop</Item>
      </Menu>
    );
  }
);
